import { Box, Grid, Stack } from '@mui/material';
import { CourseCard } from '@/components/partial/course-card';
import { Pagination } from '@/components/shared/pagination';
import { useCatalogSearch } from '@/data/catalog/search';
import type { Course } from '@/models/catalog/course';
import { useCatalogFilters } from '@/packages/courses/context/catalog-filters';
import { urlParamsToCatalogRequest } from '@/packages/courses/utils/url-params';

const EmptyState = () => {
  return (
    <Box py={6} textAlign="center">
      <Stack alignItems="center" spacing={1}>
        <Box component="p" color="text.secondary">
          Nenhum curso encontrado. Tente ajustar os filtros.
        </Box>
      </Stack>
    </Box>
  );
};

export const CatalogList = () => {
  const { params, actions } = useCatalogFilters();
  const request = urlParamsToCatalogRequest(params);
  const { data, isFetching } = useCatalogSearch(request);

  const items: Course[] = data?.items ?? [];
  const showcase: Course[] = data?.showcase ?? [];
  const pagination = data?.pagination ?? {
    total: 0,
    total_pages: 0,
    page: 1,
    page_size: 50,
  };

  const handlePageChange = (page: number) => {
    actions.setPage(page);
  };

  const hasResults = items.length > 0 || showcase.length > 0;

  return (
    <Stack spacing={3}>
      {isFetching && (
        <Grid container spacing={2} columns={{ xs: 1, md: 2, lg: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid size={1} key={i}>
              <Box
                key={i}
                sx={{
                  width: { xs: '100%', sm: 275 },
                  height: 270,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {!isFetching && !hasResults && <EmptyState />}
      {!isFetching && hasResults && (
        <>
          <Grid container spacing={2} columns={{ xs: 1, md: 2, lg: 3 }}>
            {items.map((course) => (
              <Grid size={1} key={course.id}>
                <CourseCard data={course} />
              </Grid>
            ))}
          </Grid>
          {pagination.total_pages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                pageChange={handlePageChange}
              />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCatalogListData = () => {
  const { params } = useCatalogFilters();
  const request = urlParamsToCatalogRequest(params);
  const { data, isFetching } = useCatalogSearch(request);
  const totalCount = data?.pagination?.total ?? 0;
  return { totalCount, isFetching };
};
